// Shelf numbers are WALL rows (boards clustered by height across the whole run), so on a ragged
// wall a bay does not carry every number: bay A's boards can be rows 2, 4 and 6 while bay B's
// are 1, 3, 5 and 7. A gap in the wall numbers is then not a gap on the shelf the shopper sees,
// and "the top two shelves" is {2, 4} in one bay and {1, 3} in the next. Anything that asks
// whether shelves run together, or whether two bays put a band on the same shelves, counts the
// shelves of each bay instead.
import type { StudioRunGeometry, StudioShelfGeometry } from "./studioTypes";

const bayShelfIndex = new WeakMap<StudioRunGeometry, Map<number, Map<number, number>>>();

/**
 * The shelf a shopper standing at `bay` would count: 1 = that bay's top board. On a wall whose
 * bays all carry the same boards this is the wall row itself. A row the bay has no board on reads
 * as the bay's next board down, where a pack there would come to rest. A run without bay data
 * keeps the wall number.
 */
export const bayShelfOf = (
  run: StudioRunGeometry | undefined,
  bay: number | undefined,
  rank: number,
): number => {
  if (!run || bay === undefined || !run.shelves.some((shelf) => shelf.spans)) return rank;
  /* Scoring runs inside the move loop: index each bay's boards once per run. */
  let byBay = bayShelfIndex.get(run);
  if (!byBay) {
    byBay = new Map();
    bayShelfIndex.set(run, byBay);
  }
  let own = byBay.get(bay);
  if (!own) {
    const ranks = run.shelves
      .filter((shelf) => !shelf.spans || shelf.spans.some((span) => span.bays.includes(bay)))
      .map((shelf) => shelf.rank)
      .sort((a, b) => a - b);
    own = new Map(ranks.map((wallRank, index) => [wallRank, index + 1]));
    byBay.set(bay, own);
  }
  const carried = own.get(rank);
  if (carried !== undefined || own.size === 0) return carried ?? rank;
  let above = 0;
  for (const wallRank of own.keys()) if (wallRank < rank) above += 1;
  return Math.min(above + 1, own.size);
};

const carries = (shelf: StudioShelfGeometry, bay: number) =>
  !shelf.spans || shelf.spans.some((span) => span.bays.includes(bay));

/** The board `shelf` stands as under `bay`: the bay's own where a notched bay's board stands off
 *  the row (`StudioShelfGeometry.bayBoards`), else the row's. */
const boardUnder = (
  shelf: StudioShelfGeometry,
  bay: number | undefined,
): { y: number; thickness: number } => {
  const own = bay === undefined ? undefined : shelf.bayBoards?.find((board) => board.bay === bay);
  return own ?? { y: shelf.yCm, thickness: shelf.thicknessCm };
};

/** Clear height above `shelf` under `bay`: up to the next board that bay carries, at that bay's
 *  own heights (a notched bay's boards stand off the row's median height). Without bay data
 *  every row is a board everywhere. */
const openingUnder = (
  run: StudioRunGeometry,
  shelf: StudioShelfGeometry,
  bay: number | undefined,
): number => {
  let above: StudioShelfGeometry | undefined;
  for (const candidate of run.shelves) {
    if (candidate.rank >= shelf.rank) continue;
    if (bay !== undefined && !carries(candidate, bay)) continue;
    if (!above || candidate.rank > above.rank) above = candidate;
  }
  const over = above ? boardUnder(above, bay) : undefined;
  const ceiling = over ? over.y - over.thickness : run.heightCm || Infinity;
  return Math.max(ceiling - boardUnder(shelf, bay).y, 0) || Infinity;
};

/** A stretch of one wall row that has a board under it: consecutive bays carrying the row. */
export interface RowWindow {
  rank: number;
  startCm: number;
  endCm: number;
  /** The lowest clear height under any bay of the window. */
  openingCm: number;
}

const boardsByRun = new WeakMap<StudioRunGeometry, readonly RowWindow[]>();
const windowsByRun = new WeakMap<StudioRunGeometry, readonly RowWindow[]>();

/** Every window a product can stand in, by row then x: the boards less their dead strips. A row
 *  every bay carries, with no strip, is one window the run's full width. */
export const rowWindowsOf = (run: StudioRunGeometry): readonly RowWindow[] => {
  const cached = windowsByRun.get(run);
  if (cached) return cached;
  const deadOf = new Map(run.shelves.map((shelf) => [shelf.rank, shelf.deadSpans ?? []]));
  const windows = boardWindowsOf(run).flatMap((board) => {
    const dead = [...(deadOf.get(board.rank) ?? [])].sort((a, b) => a.xCm - b.xCm);
    const live: RowWindow[] = [];
    let at = board.startCm;
    for (const strip of dead) {
      const to = Math.min(strip.xCm, board.endCm);
      if (to > at) live.push({ ...board, startCm: at, endCm: to });
      at = Math.max(at, strip.xCm + strip.widthCm);
    }
    if (board.endCm > at) live.push({ ...board, startCm: at, endCm: board.endCm });
    return live;
  });
  windowsByRun.set(run, windows);
  return windows;
};

/** The boards of each row, dead strips included: a pole does not part one board into two. */
const boardWindowsOf = (run: StudioRunGeometry): readonly RowWindow[] => {
  const cached = boardsByRun.get(run);
  if (cached) return cached;
  const windows = windowsOfRun(run);
  boardsByRun.set(run, windows);
  return windows;
};

const windowsOfRun = (run: StudioRunGeometry): readonly RowWindow[] =>
  [...run.shelves]
    .sort((a, b) => a.rank - b.rank)
    .flatMap((shelf) => {
      // A row whose boards hold no product has spans and none of them: no window at all.
      if (shelf.spans?.length === 0) return [];
      const spans = shelf.spans ?? [];
      const whole = spans.length === 0 || (spans.length === 1 && spans[0].bays.length >= run.bays);
      const openingOf = (bays: readonly number[]) =>
        bays.length === 0
          ? openingUnder(run, shelf, undefined)
          : Math.min(...bays.map((bay) => openingUnder(run, shelf, bay)));
      if (whole) {
        return [
          {
            rank: shelf.rank,
            startCm: 0,
            endCm: run.widthCm,
            openingCm: openingOf(spans[0]?.bays ?? []),
          },
        ];
      }
      return spans.map((span) => ({
        rank: shelf.rank,
        startCm: span.xCm,
        endCm: span.xCm + span.widthCm,
        openingCm: openingOf(span.bays),
      }));
    });

/** The tallest clear height anywhere along a row. */
export const rowOpeningOf = (run: StudioRunGeometry, rank: number): number | undefined => {
  const openings = rowWindowsOf(run)
    .filter((window) => window.rank === rank)
    .map((window) => window.openingCm);
  return openings.length > 0 ? Math.max(...openings) : undefined;
};

/** The shelf length a row really offers: its windows, not the run's width. */
export const rowCapacityOf = (run: StudioRunGeometry, rank: number): number =>
  rowWindowsOf(run)
    .filter((window) => window.rank === rank)
    .reduce((sum, window) => sum + window.endCm - window.startCm, 0);

/** Which of a row's windows is nearest x, so two packs on different indices are separated by a
 *  bay with no board on that row. A pack past a board's end still stands on that board. */
export const boardIndexAt = (
  run: StudioRunGeometry | undefined,
  rank: number,
  x: number,
): number => {
  if (!run) return 0;
  const windows = boardWindowsOf(run).filter((window) => window.rank === rank);
  const gap = (window: RowWindow) => Math.max(window.startCm - x, x - window.endCm, 0);
  let nearest = 0;
  windows.forEach((window, index) => {
    if (gap(window) < gap(windows[nearest])) nearest = index;
  });
  return nearest;
};
